import { useEffect } from 'react';
import { SettlementTile } from '../../components/settlement-tile';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { countryConfig } from '../../country';
import { WatchButton } from '../../components/watch-button';
import {
  CompanyDataLink,
  WinnerContactCappedNote,
  WinnerContactLines,
} from '../../components/winner-contact';
import { GoodStandingBadge } from '../../components/good-standing-badge';
import { BuyerTrustStrip } from '../../components/buyer-trust-strip';
import { SupplierContracts } from '../../components/supplier-contracts';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { Seo } from '../../components/seo';
import { localePath } from '../../i18n/locale-path';
import { useLocale, useT } from '../../i18n/locale-context';
import { useAuth } from '../../lib/auth-context';
import { usePageTitle } from '../../lib/page-title';
import { tenderPath } from '../../lib/slug';
import { isNameableRow } from '../../lib/recent-rows';
import {
  getSupplierProfile,
  getPublicSupplierProfile,
  MIN_SUPPLIER_AWARDS,
  type SupplierProfile,
  type PublicSupplierProfile,
} from '../../lib/catalog-client';
import { track } from '../../lib/funnel';
import { formatBudget, formatBudgetCompact, formatDeadline } from '../../lib/format';
import { localizeRegion } from '../../lib/regions';
import { shortenOrgName } from '../../lib/org-name';
import { ORG_ID_PROPERTY } from '../../lib/tin';
import { countryNameEn } from '../../lib/country-name-en';

const KICKER = 'text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft';

/** Pro-gated public page for a supplier company (by ИНН): scale, tenure, top
 *  buyers, category mix, region reach, plus a debarment flag. Reached from the
 *  supplier names in the buyer-concentration graph and award outcomes. */
export function SupplierProfile() {
  const { tin } = useParams<{ tin: string }>();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const t = useT();
  const { user } = useAuth();
  const authed = user != null;
  usePageTitle(t('supplierProfile.title'));

  // Landing here from a mid-page supplier link keeps the previous scroll offset;
  // the app scrolls inside <main> (not the window), so reset that on entry.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.querySelector('main')?.scrollTo({ top: 0 });
  }, [tin]);

  // Pro value-realization — an authed user opened the supplier intelligence page.
  useEffect(() => {
    if (authed) track('supplier_profile_viewed', { tin });
  }, [tin, authed]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['supplier-profile', tin],
    queryFn: () => getSupplierProfile(tin!),
    enabled: authed && !!tin,
  });

  // Anonymous tier — ALWAYS enabled. Turns the page from a bare Pro wall into a
  // public profile with aggregates (matching the buyer page), so the /companies
  // directory no longer dead-ends on supplier rows for free users.
  const publicQuery = useQuery({
    queryKey: ['supplier-public', tin],
    queryFn: () => getPublicSupplierProfile(tin!),
    enabled: !!tin,
    staleTime: 10 * 60 * 1000,
  });
  const pub = publicQuery.data?.profile ?? null;

  const locked = !authed || !data || data.locked;
  const profile = !locked && !data.locked ? data.profile : null;

  /*
   * The Pro wall on this page never emitted anything.
   *
   * `supplierProfile` was in the paywall vocabulary from the start and had zero
   * rows, which reads as "this wall doesn't exist" when in fact it is one of the
   * strongest Pro surfaces — so wall→CTA conversion here could not be computed
   * at all. Fires once per (tin, lock state), not on every render.
   */
  useEffect(() => {
    if (locked) track('paywall_shown', { wall: 'supplierProfile', tin });
  }, [locked, tin]);

  // Index only what is worth indexing — and the SERVER decides, so this page and
  // `/suppliers/index` cannot disagree. The verdict rides on the public payload
  // (`company-index-rule.ts`: >=2 lifetime awards OR >=1 in the last 24 months).
  //
  // The fallback is for the one window where the field is absent: a web-only
  // merge renders against the previous API (`prerender-tenders` needs deploy-api
  // only OPTIONALLY). Treating `undefined` as false there would ship ~24k pages
  // with `noindex` for a cycle.
  //
  // `pub` is server-seeded by the prerender, so this is decided in the CRAWLED
  // HTML — an unseeded page stays noindex with a generic title.
  const indexable = pub != null && (pub.indexable ?? pub.totalContracts >= MIN_SUPPLIER_AWARDS);
  const seoName = pub?.name ?? null;
  // Distinctive name for <title>/<description>: the registered legal form
  // («Общество с ограниченной ответственностью …» / «… MAS'ULIYATI CHEKLANGAN
  // JAMIYAT») otherwise eats the 68-char title before the company's own name is
  // reached. The FULL name stays on JSON-LD `name`, which is for entity matching
  // and has no length budget. Same treatment the buyer page already had.
  const seoShort = seoName ? shortenOrgName(seoName).short : null;
  // The facts the title/description quote. `formatBudgetCompact` is manual (not
  // `Intl.NumberFormat` compact), so it does not hit the missing-`uz`-CLDR trap
  // that renders «1.9G» in Chrome while passing every Node test.
  const seoParams = pub
    ? {
        name: seoShort ?? pub.tin,
        tin: pub.tin,
        contracts: pub.totalContracts,
        buyers: pub.distinctBuyers,
        value: `${formatBudgetCompact(String(Math.round(pub.totalValueUzs)), locale) ?? '0'} ${t(
          'tender.budget.unit',
        )}`,
        period: awardPeriod(pub.firstAwardAt, pub.lastAwardAt),
      }
    : null;

  // Absolute, locale-correct URL for a bare in-app path (for canonical/JSON-LD).
  const supplierPath = `/supplier/${tin}`;
  const absUrl = (p: string) => `${countryConfig.brand.siteUrl}${localePath(p, locale)}`;

  // Structured data, emitted ONLY for indexable suppliers (same bar as the meta
  // robots gate), mirroring the buyer page. A supplier is a private company, so
  // this is an Organization (not GovernmentOrganization); the award record it
  // anchors is public, and a breadcrumb clarifies site structure.
  const jsonLd =
    indexable && pub && seoName
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              name: seoName,
              identifier: { '@type': 'PropertyValue', propertyID: ORG_ID_PROPERTY, value: tin },
              url: absUrl(supplierPath),
              ...(pub.region
                ? { areaServed: { '@type': 'AdministrativeArea', name: pub.region } }
                : {}),
              subjectOf: {
                '@type': 'Dataset',
                name: seoParams ? t('supplier.seoTitle', seoParams) : seoName,
                // GSC 2026-09-07: Dataset invalid without `description`, warned
                // without `license`; `Place` for spatialCoverage. Mirrors the
                // buyer page and the /prices Dataset.
                description: seoParams ? t('supplier.seoDescription', seoParams) : seoName,
                url: absUrl(supplierPath),
                license: absUrl('/offer'),
                // The deployment's country — this was «Uzbekistan» on kaztender.com too.
                spatialCoverage: { '@type': 'Place', name: countryNameEn(countryConfig.code) },
                isAccessibleForFree: true,
                inLanguage: locale,
                creator: {
                  '@type': 'Organization',
                  name: countryConfig.brand.name,
                  url: `${countryConfig.brand.siteUrl}/`,
                },
              },
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: t('nav.catalog'), item: absUrl('/') },
                { '@type': 'ListItem', position: 2, name: seoName, item: absUrl(supplierPath) },
              ],
            },
          ],
        }
      : undefined;

  return (
    <>
      <Seo
        title={seoParams ? t('supplier.seoTitle', seoParams) : t('supplierProfile.title')}
        description={
          seoParams ? t('supplier.seoDescription', seoParams) : t('supplierProfile.title')
        }
        // Self-canonical + hreflang cluster, gated on `indexable` so a thin/noindex
        // supplier stub keeps its exact prior output — same discipline as buyers.
        canonical={indexable ? supplierPath : undefined}
        lang={locale}
        jsonLd={jsonLd}
        noIndex={!indexable}
      />
      <div className="mx-auto max-w-3xl lg:max-w-6xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 text-[13px] font-semibold text-ink-muted hover:text-brand"
        >
          {t('supplierProfile.back')}
        </button>

        {authed && isLoading ? (
          <div className="h-40 animate-pulse rounded-lg border border-edge bg-bone-surface shadow-m-sm" />
        ) : locked && pub ? (
          // Not entitled, but the aggregates are public — so the page carries
          // real content instead of a bare wall, and the upsell sits BELOW it.
          <>
            <PublicSupplierBody pub={pub} locale={locale} t={t} />
            <section className="mt-5 rounded-lg border border-edge bg-bone-surface p-6 text-center shadow-m-sm">
              <p className="mb-4 text-[14px] text-ink-muted">{t('supplierProfile.locked')}</p>
              <Link
                to={authed ? '/plans' : '/register'}
                className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-6 text-[14px] font-bold text-white hover:bg-brand-mid sm:w-auto"
              >
                {authed ? t('supplierProfile.lockedCta') : t('register.createAccount')}
              </Link>
            </section>
            {/* Page footer: the opt-out / correction link (owner, 2026-10-07) —
                on the PUBLIC tier too, since that is the page a company finds
                itself on from a name search. The page's last line, small and
                set apart (Igor, 10.10), like on the lot page. */}
            {tin && <CompanyDataLink tin={tin} className="mt-10 pb-2" />}
          </>
        ) : locked && publicQuery.isLoading ? (
          <div className="h-40 animate-pulse rounded-lg border border-edge bg-bone-surface shadow-m-sm" />
        ) : locked ? (
          // Public fetch failed or returned nothing — fall back to the bare wall.
          <section className="rounded-lg border border-edge bg-bone-surface p-6 text-center shadow-m-sm">
            <h1 className="mb-1 text-[18px] font-bold text-ink">{t('supplierProfile.title')}</h1>
            <p className="mb-4 text-[14px] text-ink-muted">{t('supplierProfile.locked')}</p>
            <Link
              to={authed ? '/plans' : '/register'}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-brand px-6 text-[14px] font-bold text-white hover:bg-brand-mid sm:w-auto"
            >
              {authed ? t('supplierProfile.lockedCta') : t('register.createAccount')}
            </Link>
          </section>
        ) : isError || !profile ? (
          <section className="rounded-lg border border-edge bg-bone-surface p-6 shadow-m-sm">
            <h1 className="mb-1 text-[18px] font-bold text-ink">{t('supplierProfile.title')}</h1>
            <p className="text-[14px] text-ink-muted">{t('supplierProfile.empty')}</p>
          </section>
        ) : (
          <ProfileBody profile={profile} locale={locale} t={t} />
        )}
      </div>
    </>
  );
}

/**
 * «2021\u20132026», or a single year when the whole record sits in one.
 *
 * The description has to say WHEN — 37 contracts across five years and 37 in a
 * month are different claims — and a year range is the only period both locales
 * render identically, with no month name and no `Intl` dependency.
 */
function awardPeriod(first: string | null, last: string | null): string {
  const y = (d: string | null): string | null => (d ? String(new Date(d).getUTCFullYear()) : null);
  const a = y(first);
  const b = y(last);
  if (!a && !b) return '';
  if (!a || !b || a === b) return (a ?? b) as string;
  return `${a}\u2013${b}`;
}

function ProfileBody({
  profile: p,
  locale,
  t,
}: {
  profile: SupplierProfile;
  locale: string;
  t: (k: string, params?: Record<string, string | number>) => string;
}) {
  const unit = t('tender.budget.unit');
  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-6">
      {/* Left column — inverted header + main list (top buyers) */}
      <main className="flex min-w-0 flex-col gap-5">
        <section className="rounded-lg bg-brand p-5 shadow-m-sm sm:p-6">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.05em] text-white/60">
            {t('supplierProfile.title')}
          </div>
          <h1 className="text-[20px] font-extrabold leading-tight text-white [overflow-wrap:anywhere] sm:text-[24px]">
            {p.name ?? p.tin}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-white/55">
            <span className="tabular-nums">{t('org.tinNumber', { number: p.tin })}</span>
            {p.sector && <span>· {p.sector}</span>}
            {p.region && <span>· {localizeRegion(p.region, locale)}</span>}
          </div>
          {/* The company's own phone/email — only on this PAID payload (the API
              adds `contact` past the profile's entitlement gate, and the public
              body below never receives one). */}
          <WinnerContactLines
            contact={p.contact}
            tone="onBrand"
            labelKey="tender.contactInfo"
            className="mt-2"
            testId="supplier-contact"
            surface="supplier"
          />
          {p.contactCapped && (
            // Over today's winner-contact ceiling (server-side, shared with the
            // lot page): say so rather than let the line vanish as «no contact».
            <WinnerContactCappedNote
              tone="onBrand"
              className="mt-2"
              testId="supplier-contact-capped"
            />
          )}
          {/* Composite trust badge — same posture as the buyer profile. */}
          <GoodStandingBadge goodStanding={p.goodStanding} className="mt-2.5" />
          {/* Follow this org's awards — placed here because the profile is the one
              screen where the reader already has the ИНН in hand. */}
          <div className="mt-4">
            <WatchButton tin={p.tin} kind="winner" label={p.name ?? null} />
          </div>
          {p.debarred && (
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-pill border border-white/25 bg-white/10 px-2.5 py-1 text-[12px] font-bold text-white">
              ⚠ {t('supplierProfile.debarred')}
            </p>
          )}
          {/* Igor, 10.10: «Это моя компания?» (the claim flow) is gone from this
              page — nobody could tell when to press it. What a company here
              actually needs is ONE quiet line at the bottom of the card that
              holds its contacts: hide the phone or fix the data, through the
              support form (owner, 2026-10-07). Claiming stays in the cabinet
              (`ClaimCompanyCard`). */}
          <CompanyDataLink tin={p.tin} tone="onBrand" className="mt-5" />
        </section>

        {/* Soliq registry trust — «О поставщике» chips (status · НДС · уставный
            фонд · работает с). `empty:hidden` drops it when no field resolves. */}
        <div className="empty:hidden">
          <BuyerTrustStrip
            kind="supplier"
            status={p.status}
            vatPayer={p.vatPayer}
            charterFundUzs={p.charterFundUzs}
            statusGroup={p.statusGroup}
            regDate={p.regDate}
            liquidationDate={p.liquidationDate}
            dxmapRateType={p.dxmapRateType}
            dxmapSector={p.dxmapSector}
            kgdFlags={p.kgdFlags}
          />
        </div>

        {p.topBuyers.length > 0 && (
          <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
            <div className={`mb-3 ${KICKER}`}>{t('supplierProfile.topBuyers')}</div>
            <ul className="divide-y divide-edge-light">
              {p.topBuyers.map((b) => (
                <li
                  key={b.tin}
                  className="flex items-baseline justify-between gap-3 py-2.5 first:pt-0"
                >
                  <Link
                    to={`/buyer/${encodeURIComponent(b.tin)}`}
                    className="min-w-0 truncate text-[14px] font-semibold text-brand hover:underline"
                    title={b.name ?? b.tin}
                  >
                    {b.name ?? b.tin}
                  </Link>
                  <span className="shrink-0 text-[13px] text-ink-muted">
                    {t('supplierProfile.nContracts', { n: b.contracts })} ·{' '}
                    <span className="font-semibold tabular-nums text-ink-soft">
                      {formatBudgetCompact(String(Math.round(b.valueUzs)), locale)} {unit}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Every contract, newest first, each with its buyer (2026-10-12) — the
            paid view listed none before. Pro only: the public body below keeps
            its newest-8 window, because «who won, at what price» is the wall. */}
        <SupplierContracts key={p.tin} tin={p.tin} />
      </main>

      {/* Right rail — scale + breakdown */}
      <aside className="mt-5 flex flex-col gap-5 lg:mt-0">
        <section className="overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm">
          <dl className="grid grid-cols-2 divide-x divide-y divide-edge-light">
            <Stat label={t('supplierProfile.contracts')} value={String(p.totalContracts)} />
            <Stat
              label={t('supplierProfile.totalValue')}
              value={`${formatBudgetCompact(String(Math.round(p.totalValueUzs)), locale)} ${unit}`}
              title={formatBudget(String(Math.round(p.totalValueUzs))) ?? undefined}
            />
            <Stat
              label={t('supplierProfile.avgValue')}
              value={
                p.avgValueUzs != null
                  ? `${formatBudgetCompact(String(Math.round(p.avgValueUzs)), locale)} ${unit}`
                  : '—'
              }
            />
            <Stat label={t('supplierProfile.buyers')} value={String(p.distinctBuyers)} />
          </dl>
          {/* Settlement delta — nothing renders below the sample floor. */}
          <div className="p-3">
            <SettlementTile settlement={p.settlement} />
          </div>
          {/* ⚠️ Rendered even with no dates. xarid-prequest publishes no award
              date at all, so a supplier whose awards all come from there had
              this block vanish — the same "missing data looks like a broken
              page" failure as the price detail sections. */}
          <div className="border-t border-edge-light px-5 py-3 text-[13px] text-ink-muted sm:px-6">
            <span className={KICKER}>{t('supplierProfile.tenure')}</span>{' '}
            {p.firstAwardAt || p.lastAwardAt ? (
              <>
                {formatDeadline(p.firstAwardAt, locale) ?? '—'} —{' '}
                {formatDeadline(p.lastAwardAt, locale) ?? '—'}
              </>
            ) : (
              t('supplierProfile.tenureUnknown')
            )}
          </div>
        </section>

        {p.categoryMix.length > 0 && (
          <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
            <div className={`mb-3 ${KICKER}`}>{t('supplierProfile.categories')}</div>
            <ul className="flex flex-col gap-2">
              {p.categoryMix.map((c) => (
                <li
                  key={c.section}
                  className="flex items-baseline justify-between gap-2 text-[13.5px]"
                >
                  <span className="min-w-0 truncate text-ink-soft" title={c.name ?? c.section}>
                    {c.name ?? c.section}
                  </span>
                  <span className="shrink-0 font-semibold tabular-nums text-ink-muted">
                    {formatBudgetCompact(String(Math.round(c.valueUzs)), locale)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        {p.regionReach.length > 0 && (
          <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
            <div className={`mb-3 ${KICKER}`}>{t('supplierProfile.regions')}</div>
            <ul className="flex flex-col gap-2">
              {p.regionReach.map((r) => (
                <li
                  key={r.region}
                  className="flex items-baseline justify-between gap-2 text-[13.5px]"
                >
                  <span className="min-w-0 truncate text-ink-soft">
                    {localizeRegion(r.region, locale)}
                  </span>
                  <span className="shrink-0 tabular-nums text-ink-muted">
                    {t('supplierProfile.nContracts', { n: r.contracts })}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </aside>
    </div>
  );
}

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="p-4 sm:p-5">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
        {label}
      </div>
      <div title={title} className="text-[17px] font-bold tabular-nums text-ink">
        {value}
      </div>
    </div>
  );
}

/** Anonymous tier — public aggregates only (no top buyers, no settlement, which
 *  are Pro). Mirrors the buyer profile's public body so the /companies directory
 *  shows real content for a supplier instead of a bare paywall. */
function PublicSupplierBody({
  pub,
  locale,
  t,
}: {
  pub: PublicSupplierProfile;
  locale: string;
  t: (k: string, params?: Record<string, string | number>) => string;
}) {
  const unit = t('tender.budget.unit');
  // See the buyer page: filter once, then guard the section on the FILTERED list
  // so an all-dashes payload drops the block instead of an empty heading.
  const recentContracts = (pub.recentContracts ?? []).filter(isNameableRow);
  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
        <p className={KICKER}>{t('supplierProfile.title')}</p>
        <h1 className="mt-1 text-[20px] font-bold leading-tight text-ink [overflow-wrap:anywhere]">
          {pub.name ?? pub.tin}
        </h1>
        <p className="mt-1 text-[13px] text-ink-muted">
          {t('org.tinNumber', { number: pub.tin })}
          {pub.sector ? ` · ${pub.sector}` : ''}
          {pub.region ? ` · ${localizeRegion(pub.region, locale) ?? pub.region}` : ''}
        </p>
        <GoodStandingBadge goodStanding={pub.goodStanding} className="mt-2" />
        <div className="mt-3 empty:hidden">
          <BuyerTrustStrip
            kind="supplier"
            status={pub.status}
            vatPayer={pub.vatPayer}
            charterFundUzs={pub.charterFundUzs}
            statusGroup={pub.statusGroup}
            regDate={pub.regDate}
            liquidationDate={pub.liquidationDate}
            dxmapRateType={pub.dxmapRateType}
            dxmapSector={pub.dxmapSector}
            kgdFlags={pub.kgdFlags}
          />
        </div>
      </section>

      <section className="overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm">
        <dl className="grid grid-cols-2 divide-x divide-y divide-edge-light">
          <Stat label={t('supplierProfile.contracts')} value={String(pub.totalContracts)} />
          <Stat
            label={t('supplierProfile.totalValue')}
            value={`${formatBudgetCompact(String(Math.round(pub.totalValueUzs)), locale)} ${unit}`}
          />
          <Stat
            label={t('supplierProfile.avgValue')}
            value={
              pub.avgValueUzs != null
                ? `${formatBudgetCompact(String(Math.round(pub.avgValueUzs)), locale)} ${unit}`
                : '—'
            }
          />
          <Stat label={t('supplierProfile.buyers')} value={String(pub.distinctBuyers)} />
        </dl>
        <div className="border-t border-edge-light px-5 py-3 text-[13px] text-ink-muted sm:px-6">
          <span className={KICKER}>{t('supplierProfile.tenure')}</span>{' '}
          {pub.firstAwardAt || pub.lastAwardAt ? (
            <>
              {formatDeadline(pub.firstAwardAt, locale) ?? '—'} —{' '}
              {formatDeadline(pub.lastAwardAt, locale) ?? '—'}
            </>
          ) : (
            t('supplierProfile.tenureUnknown')
          )}
        </div>
      </section>

      {/* «Госзакупки за 12 месяцев» (2026-09-07) — the read no registry
          aggregator has: wins and value from our award graph, median discount
          where the source publishes starting prices, and bid participation as
          an explicit FLOOR (bid detail covers ~16% of awards). Each figure
          carries its own coverage note; a bare number here would overclaim. */}
      {pub.procurement12m && (
        <section className="overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm">
          <div className="px-5 pt-5 sm:px-6">
            <div className={KICKER}>{t('supplierProfile.p12.title')}</div>
          </div>
          <dl className="grid grid-cols-2 divide-x divide-y divide-edge-light">
            <Stat label={t('supplierProfile.p12.wins')} value={String(pub.procurement12m.wins)} />
            <Stat
              label={t('supplierProfile.p12.value')}
              value={`${formatBudgetCompact(String(Math.round(pub.procurement12m.valueUzs)), locale)} ${unit}`}
            />
            {pub.procurement12m.medianDiscountPct != null && (
              <Stat
                label={t('supplierProfile.p12.discount')}
                value={`${pub.procurement12m.medianDiscountPct.toLocaleString(locale === 'uz' ? 'uz' : 'ru')}%`}
                title={t('supplierProfile.p12.discountNote', {
                  n: pub.procurement12m.pricedAwards,
                })}
              />
            )}
            {pub.procurement12m.medianParticipants != null && (
              <Stat
                label={t('supplierProfile.p12.participants')}
                value={String(pub.procurement12m.medianParticipants)}
              />
            )}
          </dl>
          {pub.procurement12m.participations > 0 && (
            <div className="border-t border-edge-light px-5 py-3 text-[13px] text-ink-soft sm:px-6">
              <span className={KICKER}>{t('supplierProfile.p12.bids')}</span>{' '}
              {t('supplierProfile.p12.bidsNote', {
                wins: pub.procurement12m.winsWithDetail,
                n: pub.procurement12m.participations,
              })}
            </div>
          )}
          <div className="border-t border-edge-light px-5 py-2.5 text-[12px] text-ink-muted sm:px-6">
            {t('supplierProfile.p12.source')}
          </div>
        </section>
      )}

      {/* Recent contracts, PUBLIC (2026-09-07): what this supplier delivered,
          for how much, when — each a link into the catalogue. The buyer is not
          here; the named pairing stays on the Pro tier. Crawlable on purpose:
          the GSC audit measured 179 words and no content link on these pages.
          Optional in the type — an API one deploy behind sends none. */}
      {recentContracts.length > 0 && (
        <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
          <div className={`mb-3 ${KICKER}`}>{t('supplierProfile.recentContracts')}</div>
          <ul className="divide-y divide-edge-light">
            {recentContracts.map((c, i) => (
              <li key={c.tenderId ?? i} className="py-2.5 first:pt-0">
                <div className="flex items-baseline justify-between gap-3">
                  {c.tenderId ? (
                    <Link
                      // Canonical, not the bare id — see the buyer page's note.
                      to={tenderPath(c.slug, c.tenderId)}
                      className="min-w-0 truncate text-[14px] font-semibold text-brand hover:underline"
                      title={c.title ?? c.tenderId}
                    >
                      {c.title ?? c.tenderId}
                    </Link>
                  ) : (
                    <span className="min-w-0 truncate text-[14px] font-semibold text-ink">
                      {c.title}
                    </span>
                  )}
                  <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink-soft">
                    {formatBudgetCompact(String(Math.round(c.valueUzs)), locale) ?? '0'}{' '}
                    {t('tender.budget.unit')}
                  </span>
                </div>
                {c.awardedAt && (
                  <div className="mt-0.5 text-[12px] text-ink-muted">
                    {formatDeadline(c.awardedAt, locale) ?? ''}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {pub.categoryMix.length > 0 && (
        <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
          <div className={`mb-3 ${KICKER}`}>{t('supplierProfile.categories')}</div>
          <ul className="flex flex-col gap-2">
            {pub.categoryMix.map((c) => (
              <li
                key={c.section}
                className="flex items-baseline justify-between gap-2 text-[13.5px]"
              >
                <span className="min-w-0 truncate text-ink-soft" title={c.name ?? c.section}>
                  {c.name ?? c.section}
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-ink-muted">
                  {formatBudgetCompact(String(Math.round(c.valueUzs)), locale)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pub.regionReach.length > 0 && (
        <section className="rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
          <div className={`mb-3 ${KICKER}`}>{t('supplierProfile.regions')}</div>
          <ul className="flex flex-col gap-2">
            {pub.regionReach.map((r) => (
              <li
                key={r.region}
                className="flex items-baseline justify-between gap-2 text-[13.5px]"
              >
                {/* An internal link into the /region/:code hub whenever the
                    registry name resolves to a reference row. Company pages had
                    NO outbound link into the catalogue at all: a crawler that
                    arrived on a supplier profile from a name query had nowhere
                    to go, and the region hubs had nothing pointing at them but
                    the sitemap. `code` is optional — an unresolved free-text
                    region name renders as text, never as a dead href. */}
                {r.code ? (
                  <Link
                    to={`/region/${r.code}`}
                    className="min-w-0 truncate text-ink-soft hover:text-brand hover:underline"
                  >
                    {localizeRegion(r.region, locale)}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate text-ink-soft">
                    {localizeRegion(r.region, locale)}
                  </span>
                )}
                <span className="shrink-0 tabular-nums text-ink-muted">
                  {t('supplierProfile.nContracts', { n: r.contracts })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
